import React, {useEffect, useMemo, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import SideNav from 'components/layout-components/SideNav';
import TopNav from 'components/layout-components/TopNav';
import Loading from 'components/shared-components/Loading';
import MobileNav from 'components/layout-components/MobileNav';
import HeaderNav from 'components/layout-components/HeaderNav';
import PageHeader from 'components/layout-components/PageHeader';
import Footer from 'components/layout-components/Footer';
import AppViews from 'views/app-views';
import {Grid, Layout} from 'antd';

import navigationConfig from 'configs/NavigationConfig';
import {
  DIR_LTR,
  DIR_RTL,
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
  SIDE_NAV_COLLAPSED_WIDTH,
  SIDE_NAV_WIDTH,
} from 'constants/ThemeConstant';
import utils from 'utils';
import {useThemeSwitcher} from 'react-css-theme-switcher';
import FullscreenLoading from 'components/shared-components/FullscreenLoading';
import TutorialModal from 'views/app-views/dashboard/TutorialModal';
import {completeTutorial} from 'redux/actions';
import PusherProvider from 'services/Pusher';

const { Content } = Layout;
const { useBreakpoint } = Grid;

export const AppLayout = ({ navCollapsed, navType, location, direction }) => {
  const { token } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const { tutorial } = useSelector((s) => s.auth);
  const currentRouteInfo = utils.getRouteInfo(
    navigationConfig,
    location.pathname
  );
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes('lg');
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;
  const getLayoutGutter = () => {
    if (isNavTop || isMobile) {
      return 0;
    }
    return navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
  };

  const { status } = useThemeSwitcher();
  const [showTutorial, setShowTutorial] = useState(false);

  const pusherConfig = useMemo(
    () => ({
      cluster: 'mt1',
      broadcaster: 'pusher',
      channelAuthorization: {
        endpoint: `${process.env.REACT_APP_API_URL}auth/pusher`,
        forceTLS: false,
        encrypted: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    }),
    [token]
  );

  useEffect(() => {
    if (tutorial && tutorial.vimeo_embed_code) {
      setShowTutorial(true);
    }
  }, [tutorial]);

  if (status === 'loading') {
    return <Loading cover="page" />;
  }

  const getLayoutDirectionGutter = () => {
    if (direction === DIR_LTR) {
      return { paddingLeft: getLayoutGutter() };
    }
    if (direction === DIR_RTL) {
      return { paddingRight: getLayoutGutter() };
    }
    return { paddingLeft: getLayoutGutter() };
  };

  const handleOnCompleteTutorial = () => {
    dispatch(completeTutorial(tutorial.id));
    setShowTutorial(false);
  };

  return (
    <PusherProvider
      pusher={new window.Pusher(process.env.REACT_APP_PUSHER_KEY, pusherConfig)}
    >
      <Layout>
        <Layout className="app-container">
          {isNavSide && !isMobile ? (
            <SideNav localization={false} routeInfo={currentRouteInfo} />
          ) : null}
          <Layout className="app-layout" style={getLayoutDirectionGutter()}>
            <HeaderNav isMobile={isMobile} />
            {isNavTop && !isMobile ? (
              <TopNav routeInfo={currentRouteInfo} />
            ) : null}
            <div className={`app-content ${isNavTop ? 'layout-top-nav' : ''}`}>
              <PageHeader
                display={currentRouteInfo?.breadcrumb}
                title={currentRouteInfo?.title}
              />
              <Content>
                <AppViews />
              </Content>
            </div>
            <Footer />
          </Layout>
        </Layout>
        {isMobile && <MobileNav />}
      </Layout>
      <FullscreenLoading />
      <TutorialModal
        visible={showTutorial}
        onComplete={handleOnCompleteTutorial}
        onCancel={() => setShowTutorial(false)}
      />
    </PusherProvider>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale } = theme;
  return { navCollapsed, navType, locale };
};

export default connect(mapStateToProps)(React.memo(AppLayout));
